import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { gridSizeMdLg5, gridSizeMdLg7, useLoaders } from '@/shared';
import { CacheKeysPreventaEnum, useFetchFlotas } from '@/shared/actions/erp';
import {
  CustomAutocomplete,
  CustomTextFieldNoForm,
  SampleDatePicker,
} from '@/shared/components/ui';
import { AlertSeverityEnum,  IDataPreventa } from '@/shared/interfaces';
import { useGenericCountdownStore } from '@/store/erp';
import { useInstallScheduleStore } from '@/store/erp/planeacion';
import { useUiStore } from '@/store/ui';
import { useInstallSchedule } from '../../hooks/useInstallSchedule';
import ConfirmInstallScheduleModal from './ConfirmInstallScheduleModal';
import TimerAndHourInstallScheduleComponent from './TimerAndHourInstallScheduleComponent';
import { Flota } from '@/shared/interfaces/erp/mantenimiento-operaciones';

export type InstallScheduleComponentProps = {
  // form: UseFormReturn<SaveFormDataPreagendamiento>;
  form: any;
  preventa: IDataPreventa;
};

const InstallScheduleComponent: React.FC<InstallScheduleComponentProps> = ({
  form,
  preventa,
}) => {
  ///* hooks -----------------------------
  const { onChangeFechaInstalacion } = useInstallSchedule({
    form,
    preventa,
  });

  ///* local state -----------------------------
  const [isOpenModal, setIsOpenModal] = useState(false);

  ///* global state -----------------------------
  const timeMapPreagenda = useInstallScheduleStore(s => s.timeMap);
  const limitDates = useInstallScheduleStore(s => s.limitDates);
  const selectedDate = useInstallScheduleStore(s => s.selectedDate);
  const clearAllPlanificadores = useInstallScheduleStore(s => s.clearAll);
  const isFormBlocked = useInstallScheduleStore(s => s.isFormBlocked);
  const cacheHorarioData = useInstallScheduleStore(s => s.cacheHorarioData);
  const count = useGenericCountdownStore(s => s.count);
  const setAlert = useUiStore(s => s.setAlert);
  const allTimeMaps = useInstallScheduleStore(s => s.allTimeMaps);

  ///* form -----------------------------
  const errors = form.formState.errors;
  const watchedFlota = form.watch('flota');
  const watchedFechaInstalacion = form.watch('fecha_instalacion');
  const watchedFlotaRaw = form.watch('flotaRaw');
  const watchedZone = form.watch('zona');

  ///* fetch data --------------------------
  const {
    data: flotasPagingRes,
    isLoading: isLoadingFlotas,
    isRefetching: isRefetchingFlotas,
  } = useFetchFlotas(
    {
      page_size: 300,
      zonas: watchedZone,
    },
    !!watchedZone
  );

  ///* handlers -----------------------------
  const onChangeFlota = async (flotaCode?: string) => {
    if (!flotaCode) return;
    form.setValue('fecha_instalacion', null as any);

    clearAllPlanificadores();
    //getTimeMapsByFlota(flotaCode);
  };

  // --------- timer ---------
  const minutes = Math.floor((count ?? 0) / 60)
    .toString()
    .padStart(2, '0');
  const seconds = ((count ?? 0) % 60).toString().padStart(2, '0');

  ///* effects -----------------------------
  useEffect(() => {
    if (isLoadingFlotas || isRefetchingFlotas) return;

    if (!flotasPagingRes?.data?.data?.length)
      setAlert(
        true,
        'No hay flotas disponibles para la zona seleccionada',
        AlertSeverityEnum.warning
      );
  }, [isLoadingFlotas, isRefetchingFlotas, flotasPagingRes]);

  useLoaders(isLoadingFlotas || isRefetchingFlotas);
  if (isLoadingFlotas || isRefetchingFlotas) return null;

  return (
    <>
      {isFormBlocked ? (
        <>
          <CustomTextFieldNoForm
            label="Flota"
            value={cacheHorarioData?.flotaName || watchedFlotaRaw?.nombre || ''}
            size={gridSizeMdLg7}
            disabled
          />

          <CustomTextFieldNoForm
            label="Fecha de Instalación"
            value={cacheHorarioData?.selectedDate || selectedDate || ''}
            size={gridSizeMdLg5}
            disabled
          />

          <TimerAndHourInstallScheduleComponent
            form={form}
            preventa={preventa!}
            watchedFlota={watchedFlota!}
            minutes={minutes || 0}
            seconds={seconds || 0}
            setIsOpenModal={setIsOpenModal}
          />
        </>
      ) : (
        <>
          <CustomAutocomplete<Flota>
            label="Flota"
            name="flota"
            // options
            options={flotasPagingRes?.data?.data || []}
            valueKey="nombre"
            actualValueKey="codigo"
            defaultValue={form.getValues().flota}
            isLoadingData={isLoadingFlotas || isRefetchingFlotas}
            // vaidation
            control={form.control}
            error={errors.flota}
            helperText={errors.flota?.message}
            onChangeValue={onChangeFlota}
            onChangeRawValue={flota => {
              form.setValue('flotaRaw', flota);
            }}
            disableClearable
            size={gridSizeMdLg7}
            // disabled={isFormBlocked}
          />

          {/* ================ HORARIO ================ */}
          <>
            {watchedFlota && !!limitDates ? (
              <>
                <SampleDatePicker
                  label="Fecha de Instalación"
                  name="fecha_instalacion"
                  control={form.control}
                  defaultValue={null}
                  error={form.formState.errors.fecha_instalacion}
                  helperText={form.formState.errors.fecha_instalacion?.message}
                  onChangeValue={onChangeFechaInstalacion}
                  minDate={limitDates?.at(0) || dayjs()}
                  maxDate={limitDates?.at(-1) || null}
                  size={gridSizeMdLg5}
                  shouldDisableDate={day => {
                    const isBlockedDate = !allTimeMaps?.some(
                      tm =>
                        dayjs(tm.fecha).format('YYYY-MM-DD') ===
                          dayjs(day).format('YYYY-MM-DD') && tm.horas.length
                    );

                    return isBlockedDate;
                  }}
                />

                {watchedFechaInstalacion &&
                !!timeMapPreagenda &&
                !!timeMapPreagenda?.length ? (
                  <TimerAndHourInstallScheduleComponent
                    form={form}
                    preventa={preventa!}
                    watchedFlota={watchedFlota}
                    minutes={+(minutes || 0)}
                    seconds={+(seconds || 0)}
                    setIsOpenModal={setIsOpenModal}
                  />
                ) : null}
              </>
            ) : null}
          </>

          {/* ================ MODALS ================ */}
          <ConfirmInstallScheduleModal
            flotaCode={watchedFlota!}
            isOpen={isOpenModal}
            onClose={() => setIsOpenModal(false)}
            preventa={preventa!}
            flotaName={watchedFlotaRaw?.nombre || ''}
            form={form}
            cackeKey={CacheKeysPreventaEnum.HORARIO_INSTALACION_PREAGENDA_BASE}
          />
        </>
      )}
    </>
  );
};

export default InstallScheduleComponent;
